import * as React from "react";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import { Box, Stack, TextField, Typography } from "@mui/material";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import { ParticipantSearchResult } from "../../../services/types/enterpriseParticipantApiTypes";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { GridFilterModel } from "@mui/x-data-grid-pro";

interface RegistrationsClientSearchControlProps {
    onSearchResults: (results: GridFilterModel) => void;
    open: boolean;
    data: ParticipantSearchResult[] | undefined;
    pastSearch?: ClientRegistrationPastSearch;
    updatePastSearch?: (search: ClientRegistrationPastSearch) => void;
    onReset: () => void;
    onClose: () => void;
}

export interface ClientRegistrationPastSearch {
    firstName: string;
    lastName: string;
    email: string;
    sessionTitle: string;
    sessionCustomer: string;
}

const RegistrationsClientSearchControl: React.FC<RegistrationsClientSearchControlProps> = (
    props,
) => {
    const [customer, setCustomer] = React.useState<string>(props.pastSearch?.sessionCustomer ?? "");
    const [title, setTitle] = React.useState<string>(props.pastSearch?.sessionTitle ?? "");
    const [firstName, setFirstName] = React.useState<string>(props.pastSearch?.firstName ?? "");
    const [lastName, setLastName] = React.useState<string>(props.pastSearch?.lastName ?? "");
    const [email, setEmail] = React.useState<string>(props.pastSearch?.email ?? "");
    const { logEvent } = useLogAccessEvent();

    const executeSearch = async () => {
        let filterModel: { items: { columnField: string; operatorValue: string; value: string }[] } = {
            items: []
        }

        if (firstName) {
            filterModel.items.push(
                {
                    columnField: "name",
                    operatorValue: "contains",
                    value: firstName,
                }
            );
        }
        if (lastName) {
            filterModel.items.push(
                {
                    columnField: "name",
                    operatorValue: "contains",
                    value: lastName,
                }
            );
        }
        if (email) {
            filterModel.items.push(
                {
                    columnField: "emailAddress",
                    operatorValue: "contains",
                    value: email,
                }
            );
        }
        if (customer) {
            filterModel.items.push(
                {
                    columnField: "sessionCustomer",
                    operatorValue: "contains",
                    value: customer,
                }
            );
        }
        if (title) {
            filterModel.items.push(
                {
                    columnField: "sessionTitle",
                    operatorValue: "contains",
                    value: title,
                }
            );
        }

        let p = {
            firstName: firstName,
            lastName: lastName,
            email: email,
            sessionTitle: title,
            sessionCustomer: customer,
        };

        logEvent("RegistrationSearchExecuted", null);
        props.updatePastSearch?.(p);
        props.onSearchResults(filterModel);
    };

    const resetSearch = () => {
        setFirstName("");
        setLastName("");
        setEmail("");
        setCustomer("");
        setTitle("");
        props.onReset();
    };

    return (
        <CclDrawer title={"Search"} open={props.open} onClose={props.onClose} width="standard">
            <Box sx={{ overflow: "hidden", height: 1 }}>
                <Stack spacing={4} sx={{ overflow: "auto", padding: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Participant
                    </Typography>

                    <TextField
                        fullWidth
                        label="First Name"
                        variant="outlined"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label="Last Name"
                        variant="outlined"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label="Email"
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Session
                    </Typography>

                    <TextField
                        fullWidth
                        label="Title"
                        variant="outlined"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label="Customer"
                        variant="outlined"
                        value={customer}
                        onChange={(e) => setCustomer(e.target.value)}
                    />
                </Stack>

                <CclDrawerActionBar>
                    <CclLoadingButton
                        loading={false}
                        onClick={executeSearch}
                        disabled={false}
                        sx={{ m: 1 }}
                        mode={"primary"}
                    >
                        Search
                    </CclLoadingButton>
                    <CclButton sx={{ m: 1 }} onClick={props.onClose} mode={"secondary"}>
                        Close
                    </CclButton>
                    <CclButton
                        sx={{ float: "right", m: 1, ml: "auto" }}
                        onClick={resetSearch}
                        mode={"secondary"}
                    >
                        Reset
                    </CclButton>
                </CclDrawerActionBar>
            </Box>
        </CclDrawer>
    );
};

export default RegistrationsClientSearchControl;
